import { useEffect, useState } from "react"
import { faBullhorn, faClose, faTag } from "@fortawesome/free-solid-svg-icons"
import defaultStyle from "../../../styles/defaultPageStyle.module.css"
import Button, { ButtonIcon } from "../../../components/Button/Button"
import { TitlePageResponsiveIconSmall } from "../../../components/Titles/Titles"
import THEME from "../../../configuration/Theme"
import GetSpace from "../../../utils/space"
import InputField from "../../../components/InputField/InputField"
import { INPUT_SIZE } from "../../../configuration/InputSize"
import ModalPortal from "../../../components/Modal/ModalPortal"
import { useTranslation } from "react-i18next"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import { SIZE } from "../../../configuration/Sizes"
import { useSaveCompany } from "../../../hooks/Company/useSaveCompany"
import { companyMemory } from "../../../Objects/Memory/Memory"
import { usePermission } from "../../../hooks/Permissions/usePermission"
import { Permissions } from "../../../Enum/PermissionsType"
import { CanView } from "../../../components/CanView/CanView"
import { SimpleToggle } from "../../../components/SimpleToggle/SimpleToggle"
import { PopupHelpButton } from "../../../components/Help/PopupHelpButton"

interface MarketingTrackingPopupProps {
  onClose: () => void
}

const normalizeMarketingId = (value: string) => value.trim().toUpperCase()

const normalizeNumericId = (value: string) => value.replace(/\D/g, "")

export const MarketingTrackingPopup = ({ onClose }: MarketingTrackingPopupProps) => {
  const { t } = useTranslation(["company", "helpPopup"])

  const { handleSavePublicMenuTracking } = useSaveCompany()

  const canEdite = !usePermission(Permissions.CanEditeCompany)

  const [trackingEnabled, setTrackingEnabled] = useState(false)

  const [googleTagManagerId, setGoogleTagManagerId] = useState("")

  const [googleAnalyticsMeasurementId, setGoogleAnalyticsMeasurementId] = useState("")

  const [googleAdsConversionId, setGoogleAdsConversionId] = useState("")

  const [metaPixelId, setMetaPixelId] = useState("")

  useEffect(() => {
    setTrackingEnabled(companyMemory.publicMenuTrackingEnabled)
    setGoogleTagManagerId(companyMemory.publicMenuGoogleTagManagerId)
    setGoogleAnalyticsMeasurementId(companyMemory.publicMenuGoogleAnalyticsMeasurementId)
    setGoogleAdsConversionId(companyMemory.publicMenuGoogleAdsConversionId)
    setMetaPixelId(companyMemory.publicMenuMetaPixelId)
  }, [])

  const handleTrySave = () => {
    void handleSavePublicMenuTracking(
      companyMemory.id,
      {
        publicMenuTrackingEnabled: trackingEnabled,
        publicMenuGoogleTagManagerId: normalizeMarketingId(googleTagManagerId),
        publicMenuGoogleAnalyticsMeasurementId: normalizeMarketingId(googleAnalyticsMeasurementId),
        publicMenuGoogleAdsConversionId: normalizeMarketingId(googleAdsConversionId),
        publicMenuMetaPixelId: normalizeNumericId(metaPixelId),
      },
      onClose,
    )
  }

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={defaultStyle.alert1}>
            <TitlePageResponsiveIconSmall
              title={t("marketing_tracking.title")}
              iconColor={THEME.colors.iconBGColor}
              icon={faBullhorn}
              children={
                <div className="flex items-center gap-1">
                  <PopupHelpButton
                    helpKey="pages.mystore.mycompany.marketingtrackingpopup.1"
                    variant="global"
                    title={t("helpPopup:global.marketingTracking.title")}
                    description={t("helpPopup:global.marketingTracking.description")}
                    steps={t("helpPopup:global.marketingTracking.steps", { returnObjects: true }) as string[]}
                    bullets={t("helpPopup:global.marketingTracking.bullets", { returnObjects: true }) as string[]}
                  />

                  <ButtonIcon
                    sizeNumber={SIZE.buttonCloseIconSize}
                    bgColor={THEME.colors.buttonBGCloseColor}
                    iconColor={THEME.colors.buttonFGCloseColor}
                    icon={faClose}
                    onClick={onClose}
                    size="small"
                    descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.marketingTrackingPopup.button.onClose"
                  />
                </div>
              }
            />

            <GetSpace top={10} />

            <form
              className="p-2"
              onSubmit={(e) => {
                e.preventDefault()
                e.stopPropagation()

                handleTrySave()
              }}
            >
              <Animate type={AnimationTypes.fadeInTraslate} duration="1s">
                <p className="px-1 text-sm leading-relaxed text-gray-500">{t("marketing_tracking.description")}</p>
              </Animate>

              <div className="mt-4">
                <SimpleToggle
                  title={t("marketing_tracking.enable")}
                  value={trackingEnabled}
                  disabled={canEdite}
                  onChange={(value) => setTrackingEnabled(value)}
                  animationDuration="1.15s"
                  descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.marketingTrackingPopup.toggle.enable"
                />
              </div>

              <GetSpace top={14} />

              <Animate type={AnimationTypes.fadeInTraslate} duration="1.3s">
                <p className="px-1 text-xs font-black uppercase tracking-[0.18em] text-slate-700">
                  {t("marketing_tracking.google_section")}
                </p>
              </Animate>

              <GetSpace top={10} />

              <Animate type={AnimationTypes.fadeInTraslate} duration="1.45s">
                <InputField
                  maxLength={INPUT_SIZE.COMPANY.trackingId}
                  type="text"
                  placeholder={t("marketing_tracking.gtm_placeholder")}
                  onInvalidMessage={t("marketing_tracking.invalid_gtm")}
                  pattern="^$|GTM-[A-Za-z0-9_-]+"
                  icon={faTag}
                  disable={canEdite}
                  value={googleTagManagerId}
                  onInput={(e) => {
                    e.currentTarget.value = normalizeMarketingId(e.currentTarget.value)
                  }}
                  onChange={(e) => setGoogleTagManagerId(e.target.value)}
                  descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.marketingTrackingPopup.input.gtmPlaceholder"
                />
              </Animate>

              <GetSpace top={10} />

              <Animate type={AnimationTypes.fadeInTraslate} duration="1.6s">
                <InputField
                  maxLength={INPUT_SIZE.COMPANY.trackingId}
                  type="text"
                  placeholder={t("marketing_tracking.ga_placeholder")}
                  onInvalidMessage={t("marketing_tracking.invalid_ga")}
                  pattern="^$|G-[A-Za-z0-9_-]+"
                  icon={faTag}
                  disable={canEdite}
                  value={googleAnalyticsMeasurementId}
                  onInput={(e) => {
                    e.currentTarget.value = normalizeMarketingId(e.currentTarget.value)
                  }}
                  onChange={(e) => setGoogleAnalyticsMeasurementId(e.target.value)}
                  descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.marketingTrackingPopup.input.gaPlaceholder"
                />
              </Animate>

              <GetSpace top={10} />

              <Animate type={AnimationTypes.fadeInTraslate} duration="1.75s">
                <InputField
                  maxLength={INPUT_SIZE.COMPANY.trackingId}
                  type="text"
                  placeholder={t("marketing_tracking.ads_placeholder")}
                  onInvalidMessage={t("marketing_tracking.invalid_ads")}
                  pattern="^$|AW-[A-Za-z0-9_-]+"
                  icon={faTag}
                  disable={canEdite}
                  value={googleAdsConversionId}
                  onInput={(e) => {
                    e.currentTarget.value = normalizeMarketingId(e.currentTarget.value)
                  }}
                  onChange={(e) => setGoogleAdsConversionId(e.target.value)}
                  descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.marketingTrackingPopup.input.adsPlaceholder"
                />
              </Animate>

              <GetSpace top={14} />

              <Animate type={AnimationTypes.fadeInTraslate} duration="1.9s">
                <p className="px-1 text-xs font-black uppercase tracking-[0.18em] text-slate-700">
                  {t("marketing_tracking.meta_section")}
                </p>
              </Animate>

              <GetSpace top={10} />

              <Animate type={AnimationTypes.fadeInTraslate} duration="2.05s">
                <InputField
                  maxLength={INPUT_SIZE.COMPANY.trackingId}
                  type="text"
                  inputMode="numeric"
                  placeholder={t("marketing_tracking.meta_pixel_placeholder")}
                  onInvalidMessage={t("marketing_tracking.invalid_meta_pixel")}
                  pattern="^$|[0-9]+"
                  icon={faTag}
                  disable={canEdite}
                  value={metaPixelId}
                  onInput={(e) => {
                    e.currentTarget.value = normalizeNumericId(e.currentTarget.value)
                  }}
                  onChange={(e) => setMetaPixelId(e.target.value)}
                  descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.marketingTrackingPopup.input.metaPixelPlaceholder"
                />
              </Animate>

              <Animate type={AnimationTypes.fadeInTraslate} duration="2.2s">
                <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs leading-relaxed text-amber-900">
                  <p>{t("marketing_tracking.empty_hint")}</p>
                  <p className="mt-1">{t("marketing_tracking.privacy_hint")}</p>
                </div>
              </Animate>

              <Animate type={AnimationTypes.fadeInTraslate} duration="2.35s">
                <div className={defaultStyle.divisionBar}></div>
              </Animate>

              <Animate type={AnimationTypes.fadeInTraslate} duration="2.5s">
                <section className={defaultStyle.buttonsController}>
                  <CanView permission={Permissions.CanEditeCompany}>
                    <Button
                      sizeNumber={SIZE.buttonCloseIconSize}
                      label={t("buttons:button_save")}
                      typeButton="submit"
                      type="primary"
                      size="medium"
                      bgColor={THEME.colors.buttonBGSaveColor}
                      descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.marketingTrackingPopup.button.save"
                    />
                  </CanView>
                </section>
              </Animate>
            </form>
          </div>
        </Animate>
      </div>
    </ModalPortal>
  )
}
